import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import { makeApiCall } from '../../config/api';

const VehicleInspectionReport = () => {
  const { t } = useTranslation();
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [hasMajorDefect, setHasMajorDefect] = useState(false);
  const [hasMinorDefect, setHasMinorDefect] = useState(false);
  const [trucks, setTrucks] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  const fetchTrucks = async () => {
    try {
      const response = await makeApiCall('api/routes/trucks');
      if (response.ok) {
        const result = await response.json();
        setTrucks(result.trucks || []);
      }
    } catch (err) {
      console.error('Error fetching trucks:', err);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate.toISOString());
      if (toDate) params.append('to_date', toDate.toISOString());
      if (selectedVehicleId) params.append('vehicle_id', selectedVehicleId);
      if (hasMajorDefect) params.append('has_major_defect', 'true');
      if (hasMinorDefect) params.append('has_minor_defect', 'true');

      const response = await makeApiCall(`api/reports/vehicle-inspections?${params}`);
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setData(result.data);
        } else {
          setError(t('reports.error.fetching'));
        }
      } else {
        setError(t('reports.error.fetching'));
      }
    } catch (err) {
      console.error('Error fetching vehicle inspection report:', err);
      setError(t('reports.error.fetching'));
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    setGeneratingPDF(true);
    setError(null);
    try {
      const response = await makeApiCall('api/reports/vehicle-inspections/pdf', {
        method: 'POST',
        body: JSON.stringify({
          from_date: fromDate ? fromDate.toISOString() : null,
          to_date: toDate ? toDate.toISOString() : null,
          vehicle_id: selectedVehicleId || null,
          has_major_defect: hasMajorDefect ? 'true' : null,
          has_minor_defect: hasMinorDefect ? 'true' : null,
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `vehicle-inspections-report-${Date.now()}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        setError(t('reports.error.pdf'));
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError(t('reports.error.pdf'));
    } finally {
      setGeneratingPDF(false);
    }
  };

  useEffect(() => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 30);
    setFromDate(from);
    setToDate(to);
    fetchTrucks();
  }, []);

  useEffect(() => {
    if (fromDate && toDate) {
      fetchReport();
    }
  }, [fromDate, toDate]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const majorDefectsCount = data.filter(d => d.major_issue_exist).length;
  const minorDefectsCount = data.filter(d => d.minor_issue_exist).length;

  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  const handleFromDateChange = (e) => {
    setFromDate(e.target.value ? new Date(e.target.value) : null);
  };

  const handleToDateChange = (e) => {
    setToDate(e.target.value ? new Date(e.target.value) : null);
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          {t('reports.filters')}
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              label={t('reports.fromDate')}
              type="date"
              value={formatDateForInput(fromDate)}
              onChange={handleFromDateChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              label={t('reports.toDate')}
              type="date"
              value={formatDateForInput(toDate)}
              onChange={handleToDateChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth>
              <InputLabel>{t('reports.selectVehicle')}</InputLabel>
              <Select
                value={selectedVehicleId}
                onChange={(e) => setSelectedVehicleId(e.target.value)}
                label={t('reports.selectVehicle')}
              >
                <MenuItem value="">
                  <em>{t('reports.allVehicles')}</em>
                </MenuItem>
                {trucks.map((truck) => (
                  <MenuItem key={truck.id} value={truck.id}>
                    {truck.name} {truck.plate_number && `(${truck.plate_number})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
            <Grid size={12}>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Chip
                  label={t('reports.hasMajorDefect')}
                  onClick={() => setHasMajorDefect(!hasMajorDefect)}
                  color={hasMajorDefect ? 'error' : 'default'}
                  variant={hasMajorDefect ? 'filled' : 'outlined'}
                  sx={{ cursor: 'pointer' }}
                />
                <Chip
                  label={t('reports.hasMinorDefect')}
                  onClick={() => setHasMinorDefect(!hasMinorDefect)}
                  color={hasMinorDefect ? 'warning' : 'default'}
                  variant={hasMinorDefect ? 'filled' : 'outlined'}
                  sx={{ cursor: 'pointer' }}
                />
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={fetchReport}
                  disabled={loading || !fromDate || !toDate}
                  sx={{ bgcolor: '#1976d2' }}
                >
                  {t('reports.load')}
                </Button>
                <Button
                  variant="contained"
                  startIcon={generatingPDF ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                  onClick={downloadPDF}
                  disabled={generatingPDF || data.length === 0}
                  sx={{ bgcolor: '#d32f2f' }}
                >
                  {t('reports.downloadPDF')}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {data.length > 0 && (
          <Card sx={{ mb: 3, bgcolor: '#e3f2fd' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                {t('reports.summary')}
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Chip label={`${t('reports.totalInspections')}: ${data.length}`} color="primary" />
                <Chip
                  label={`${t('reports.majorDefects')}: ${majorDefectsCount}`}
                  color="error"
                />
                <Chip
                  label={`${t('reports.minorDefects')}: ${minorDefectsCount}`}
                  color="warning"
                />
              </Stack>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : data.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              {t('reports.noData')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('reports.selectDateRange')}
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#1976d2', '& th': { color: 'white', fontWeight: 'bold' } }}>
                  <TableCell>{t('reports.vehicleName')}</TableCell>
                  <TableCell>{t('reports.plateNumber')}</TableCell>
                  <TableCell>{t('reports.vehicleType')}</TableCell>
                  <TableCell>{t('reports.inspector')}</TableCell>
                  <TableCell>{t('reports.date')}</TableCell>
                  <TableCell>{t('reports.majorDefect')}</TableCell>
                  <TableCell>{t('reports.minorDefect')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((inspection) => (
                  <TableRow key={inspection.id} hover>
                    <TableCell>{inspection.vehicle_name || 'N/A'}</TableCell>
                    <TableCell>{inspection.plate_no || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip label={inspection.vehicle_type || 'N/A'} size="small" />
                    </TableCell>
                    <TableCell>{inspection.inspector_name || 'N/A'}</TableCell>
                    <TableCell>{formatDate(inspection.created_at)}</TableCell>
                    <TableCell>
                      <Chip
                        label={inspection.major_issue_exist ? t('reports.yes') : t('reports.no')}
                        size="small"
                        color={inspection.major_issue_exist ? 'error' : 'success'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={inspection.minor_issue_exist ? t('reports.yes') : t('reports.no')}
                        size="small"
                        color={inspection.minor_issue_exist ? 'warning' : 'success'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
  );
};

export default VehicleInspectionReport;

